import { useState } from 'react';
import { SpinnerArm } from '@shark-games/shared';
import { DominoTile } from './DominoTile';
import { TileInfo } from '../../stores/gameStore';

interface DominoBoardProps {
  hasSpinner: boolean;
  spinnerId: string;
  spinnerValue: number;
  leftArm: TileInfo[];
  rightArm: TileInfo[];
  topArm: TileInfo[];
  bottomArm: TileInfo[];
  preSpinnerTiles: TileInfo[];
  openEnds: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  boneyardCount: number;
  isMyTurn: boolean;
  selectedDomino: string | null;
  validArms: SpinnerArm[];
  onArmClick: (arm: SpinnerArm) => void;
  draggingDomino: string | null;
  draggingValidArms: SpinnerArm[];
  onDrop: (arm: SpinnerArm) => void;
}

// Check if a domino is a double
function isDouble(dominoId: string): boolean {
  const [left, right] = dominoId.split('-').map(Number);
  return left === right;
}

// Get orientation for a tile - doubles are perpendicular to arm direction
function getTileOrientation(dominoId: string, arm: SpinnerArm | 'pre-spinner'): 'horizontal' | 'vertical' {
  const double = isDouble(dominoId);
  // Left/right arms and pre-spinner are horizontal rows → doubles go vertical
  // Top/bottom arms are vertical columns → doubles go horizontal
  if (arm === 'left' || arm === 'right' || arm === 'pre-spinner') {
    return double ? 'vertical' : 'horizontal';
  } else {
    return double ? 'horizontal' : 'vertical';
  }
}

// Determine if a tile needs to be flipped to show correct orientation
// The connecting end should face toward the spinner/chain
function shouldFlipTile(tile: TileInfo, arm: SpinnerArm | 'pre-spinner', position: 'start' | 'end'): boolean {
  const [firstVal, secondVal] = tile.dominoId.split('-').map(Number);

  // For horizontal tiles (left/right arms, pre-spinner):
  // - Left arm: connecting end should be on RIGHT → flip if connectingEnd equals firstVal
  // - Right arm: connecting end should be on LEFT → flip if connectingEnd equals secondVal
  // For vertical tiles (top/bottom arms):
  // - Top arm: connecting end should be on BOTTOM → flip if connectingEnd equals firstVal
  // - Bottom arm: connecting end should be on TOP → flip if connectingEnd equals secondVal

  if (arm === 'left' || arm === 'pre-spinner') {
    // For left arm and pre-spinner (left side), connecting end should be on the right
    // If connectingEnd is firstVal, we need to flip to put it on right
    // But wait - for pre-spinner on right side, it's different
    if (position === 'start') {
      // Left end of chain: connecting end should be on right
      return tile.connectingEnd === firstVal;
    } else {
      // Pre-spinner right side
      return tile.connectingEnd === secondVal;
    }
  } else if (arm === 'right') {
    // Connecting end should be on left (toward spinner)
    // firstVal is already on left, so flip if connectingEnd is secondVal
    return tile.connectingEnd === secondVal;
  } else if (arm === 'top') {
    // Connecting end should be on bottom (toward spinner)
    // secondVal is already on bottom, so flip if connectingEnd is firstVal
    return tile.connectingEnd === firstVal;
  } else if (arm === 'bottom') {
    // Connecting end should be on top (toward spinner)
    // firstVal is already on top, so flip if connectingEnd is secondVal
    return tile.connectingEnd === secondVal;
  }

  return false;
}

export function DominoBoard({
  hasSpinner,
  spinnerId,
  spinnerValue,
  leftArm,
  rightArm,
  topArm,
  bottomArm,
  preSpinnerTiles,
  openEnds,
  boneyardCount,
  isMyTurn,
  selectedDomino,
  validArms,
  onArmClick,
  draggingDomino,
  draggingValidArms,
  onDrop,
}: DominoBoardProps) {
  const [dragOverArm, setDragOverArm] = useState<SpinnerArm | null>(null);
  const showCrossLayout = hasSpinner;
  const isEmpty = !hasSpinner && preSpinnerTiles.length === 0;

  const handleDragOver = (e: React.DragEvent, arm: SpinnerArm) => {
    if (draggingDomino && draggingValidArms.includes(arm)) {
      e.preventDefault();
      setDragOverArm(arm);
    }
  };

  const handleDragLeave = () => {
    setDragOverArm(null);
  };

  const handleDrop = (e: React.DragEvent, arm: SpinnerArm) => {
    e.preventDefault();
    setDragOverArm(null);
    if (draggingDomino && draggingValidArms.includes(arm)) {
      onDrop(arm);
    }
  };

  const isDropZoneActive = (arm: SpinnerArm) => draggingDomino && draggingValidArms.includes(arm);
  const isDropZoneHighlighted = (arm: SpinnerArm) => dragOverArm === arm;

  // Calculate total open ends for display
  const totalOpenEnds = (() => {
    if (!hasSpinner && preSpinnerTiles.length === 0) return 0;
    if (!hasSpinner) {
      // Pre-spinner: left + right
      if (preSpinnerTiles.length === 1) {
        const [l, r] = preSpinnerTiles[0].dominoId.split('-').map(Number);
        return l + r;
      }
      return openEnds.left + openEnds.right;
    }
    // Post-spinner: sum all unlocked ends
    let total = 0;
    const armArrays = { left: leftArm, right: rightArm, top: topArm, bottom: bottomArm };
    for (const arm of ['left', 'right', 'top', 'bottom'] as SpinnerArm[]) {
      const end = openEnds[arm];
      if (end !== -1) {
        const armTiles = armArrays[arm];
        if (armTiles.length === 0) {
          total += spinnerValue;
        } else {
          const endTile = armTiles[armTiles.length - 1];
          const [l, r] = endTile.dominoId.split('-').map(Number);
          const isDouble = l === r;
          total += isDouble ? l + r : end;
        }
      }
    }
    // Spinner alone
    if (leftArm.length === 0 && rightArm.length === 0 && topArm.length === 0 && bottomArm.length === 0) {
      return spinnerValue * 2;
    }
    return total;
  })();

  const isArmClickable = (arm: SpinnerArm) => isMyTurn && selectedDomino && validArms.includes(arm);

  // Render linear pre-spinner layout
  if (!showCrossLayout) {
    return (
      <div className="bg-green-900/50 rounded-xl p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Table</h3>
          <span className="text-sm text-gray-400">
            Boneyard: {boneyardCount} | Ends: [{openEnds.left}] - [{openEnds.right}] = {totalOpenEnds}
          </span>
        </div>

        {isEmpty ? (
          <div className="flex items-center justify-center min-h-[120px]">
            <p className="text-gray-400">No tiles played yet</p>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1 py-4 min-h-[120px]">
            {/* Left end indicator / drop zone */}
            {(isArmClickable('left') || isDropZoneActive('left')) && (
              <button
                onClick={() => onArmClick('left')}
                onDragOver={(e) => handleDragOver(e, 'left')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'left')}
                className={`w-12 h-20 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                  ${isDropZoneHighlighted('left')
                    ? 'border-green-400 bg-green-400/40 scale-110'
                    : isDropZoneActive('left')
                      ? 'border-green-400 bg-green-400/20'
                      : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
              >
                <span className={`font-bold text-sm ${isDropZoneActive('left') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.left}</span>
              </button>
            )}

            {/* Pre-spinner tiles (horizontal, doubles vertical) */}
            {preSpinnerTiles.map((tile, index) => {
              // First tile has no connection, subsequent tiles connect based on their position
              const isLeftSide = index === 0 && preSpinnerTiles.length > 1;
              const flipped = tile.connectingEnd !== -1 && shouldFlipTile(tile, 'pre-spinner', isLeftSide ? 'start' : 'end');
              const orientation = getTileOrientation(tile.dominoId, 'pre-spinner');
              return (
                <DominoTile
                  key={`${tile.dominoId}-${index}`}
                  dominoId={tile.dominoId}
                  orientation={orientation}
                  size="md"
                  flipped={flipped}
                />
              );
            })}

            {/* Right end indicator / drop zone */}
            {(isArmClickable('right') || isDropZoneActive('right')) && (
              <button
                onClick={() => onArmClick('right')}
                onDragOver={(e) => handleDragOver(e, 'right')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'right')}
                className={`w-12 h-20 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                  ${isDropZoneHighlighted('right')
                    ? 'border-green-400 bg-green-400/40 scale-110'
                    : isDropZoneActive('right')
                      ? 'border-green-400 bg-green-400/20'
                      : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
              >
                <span className={`font-bold text-sm ${isDropZoneActive('right') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.right}</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Cross layout with spinner
  return (
    <div className="bg-green-900/50 rounded-xl p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Table (Spinner: {spinnerValue})</h3>
        <span className="text-sm text-gray-400">
          Boneyard: {boneyardCount} | Total: {totalOpenEnds} {totalOpenEnds % 5 === 0 && totalOpenEnds > 0 ? `= ${totalOpenEnds} pts!` : ''}
        </span>
      </div>

      <div className="relative min-h-[400px] flex items-center justify-center">
        {/* Cross layout container */}
        <div className="grid grid-cols-[auto_auto_auto] grid-rows-[auto_auto_auto] gap-0 items-center justify-items-center">
          {/* Top arm */}
          <div className="col-start-2 row-start-1 flex flex-col-reverse items-center gap-0 justify-end">
            {topArm.map((tile, index) => {
              const flipped = shouldFlipTile(tile, 'top', 'start');
              const orientation = getTileOrientation(tile.dominoId, 'top');
              return (
                <DominoTile
                  key={`top-${tile.dominoId}-${index}`}
                  dominoId={tile.dominoId}
                  orientation={orientation}
                  size="sm"
                  flipped={flipped}
                />
              );
            })}
            {/* Top end indicator / drop zone */}
            {openEnds.top !== -1 && (
              <div className="flex flex-col items-center gap-0">
                {(isArmClickable('top') || isDropZoneActive('top')) ? (
                  <button
                    onClick={() => onArmClick('top')}
                    onDragOver={(e) => handleDragOver(e, 'top')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'top')}
                    className={`w-10 h-16 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                      ${isDropZoneHighlighted('top')
                        ? 'border-green-400 bg-green-400/40 scale-110'
                        : isDropZoneActive('top')
                          ? 'border-green-400 bg-green-400/20'
                          : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
                  >
                    <span className={`font-bold text-xs ${isDropZoneActive('top') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.top}</span>
                  </button>
                ) : topArm.length === 0 ? null : (
                  <span className="text-xs text-gray-400 bg-shark-800 px-2 py-1 rounded">{openEnds.top}</span>
                )}
              </div>
            )}
            {openEnds.top === -1 && (
              <span className="text-xs text-gray-500 bg-shark-900/50 px-2 py-1 rounded">Locked</span>
            )}
          </div>

          {/* Left arm */}
          <div className="col-start-1 row-start-2 flex flex-row-reverse items-center gap-0 justify-end">
            {leftArm.map((tile, index) => {
              const flipped = shouldFlipTile(tile, 'left', 'start');
              const orientation = getTileOrientation(tile.dominoId, 'left');
              return (
                <DominoTile
                  key={`left-${tile.dominoId}-${index}`}
                  dominoId={tile.dominoId}
                  orientation={orientation}
                  size="sm"
                  flipped={flipped}
                />
              );
            })}
            {/* Left end indicator / drop zone */}
            <div className="flex items-center gap-0">
              {(isArmClickable('left') || isDropZoneActive('left')) ? (
                <button
                  onClick={() => onArmClick('left')}
                  onDragOver={(e) => handleDragOver(e, 'left')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'left')}
                  className={`w-16 h-10 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                    ${isDropZoneHighlighted('left')
                      ? 'border-green-400 bg-green-400/40 scale-110'
                      : isDropZoneActive('left')
                        ? 'border-green-400 bg-green-400/20'
                        : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
                >
                  <span className={`font-bold text-xs ${isDropZoneActive('left') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.left}</span>
                </button>
              ) : leftArm.length > 0 ? (
                <span className="text-xs text-gray-400 bg-shark-800 px-2 py-1 rounded">{openEnds.left}</span>
              ) : null}
            </div>
          </div>

          {/* Spinner (center) */}
          <div className="col-start-2 row-start-2 flex items-center justify-center">
            <div className="relative">
              <DominoTile
                dominoId={spinnerId}
                orientation="vertical"
                size="md"
              />
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-yellow-400 bg-yellow-400/20 px-2 py-0.5 rounded whitespace-nowrap">
                Spinner
              </div>
            </div>
          </div>

          {/* Right arm */}
          <div className="col-start-3 row-start-2 flex flex-row items-center gap-0 justify-start">
            {rightArm.map((tile, index) => {
              const flipped = shouldFlipTile(tile, 'right', 'start');
              const orientation = getTileOrientation(tile.dominoId, 'right');
              return (
                <DominoTile
                  key={`right-${tile.dominoId}-${index}`}
                  dominoId={tile.dominoId}
                  orientation={orientation}
                  size="sm"
                  flipped={flipped}
                />
              );
            })}
            {/* Right end indicator / drop zone */}
            <div className="flex items-center gap-0">
              {(isArmClickable('right') || isDropZoneActive('right')) ? (
                <button
                  onClick={() => onArmClick('right')}
                  onDragOver={(e) => handleDragOver(e, 'right')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'right')}
                  className={`w-16 h-10 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                    ${isDropZoneHighlighted('right')
                      ? 'border-green-400 bg-green-400/40 scale-110'
                      : isDropZoneActive('right')
                        ? 'border-green-400 bg-green-400/20'
                        : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
                >
                  <span className={`font-bold text-xs ${isDropZoneActive('right') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.right}</span>
                </button>
              ) : rightArm.length > 0 ? (
                <span className="text-xs text-gray-400 bg-shark-800 px-2 py-1 rounded">{openEnds.right}</span>
              ) : null}
            </div>
          </div>

          {/* Bottom arm */}
          <div className="col-start-2 row-start-3 flex flex-col items-center gap-0 justify-start">
            {bottomArm.map((tile, index) => {
              const flipped = shouldFlipTile(tile, 'bottom', 'start');
              const orientation = getTileOrientation(tile.dominoId, 'bottom');
              return (
                <DominoTile
                  key={`bottom-${tile.dominoId}-${index}`}
                  dominoId={tile.dominoId}
                  orientation={orientation}
                  size="sm"
                  flipped={flipped}
                />
              );
            })}
            {/* Bottom end indicator / drop zone */}
            {openEnds.bottom !== -1 && (
              <div className="flex flex-col items-center gap-0">
                {(isArmClickable('bottom') || isDropZoneActive('bottom')) ? (
                  <button
                    onClick={() => onArmClick('bottom')}
                    onDragOver={(e) => handleDragOver(e, 'bottom')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'bottom')}
                    className={`w-10 h-16 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors
                      ${isDropZoneHighlighted('bottom')
                        ? 'border-green-400 bg-green-400/40 scale-110'
                        : isDropZoneActive('bottom')
                          ? 'border-green-400 bg-green-400/20'
                          : 'border-yellow-400 bg-yellow-400/20 hover:bg-yellow-400/40 animate-pulse'}`}
                  >
                    <span className={`font-bold text-xs ${isDropZoneActive('bottom') ? 'text-green-400' : 'text-yellow-400'}`}>+{openEnds.bottom}</span>
                  </button>
                ) : bottomArm.length === 0 ? null : (
                  <span className="text-xs text-gray-400 bg-shark-800 px-2 py-1 rounded">{openEnds.bottom}</span>
                )}
              </div>
            )}
            {openEnds.bottom === -1 && (
              <span className="text-xs text-gray-500 bg-shark-900/50 px-2 py-1 rounded">Locked</span>
            )}
          </div>
        </div>
      </div>

      {/* Arm selection help */}
      {isMyTurn && selectedDomino && validArms.length > 1 && (
        <div className="text-center mt-4 text-yellow-400 text-sm animate-pulse">
          Click an arm to place your domino
        </div>
      )}
    </div>
  );
}
